import classNames from "classnames";
import omit from "omit.js";
import React, {cloneElement, useState, useEffect, useCallback} from "react";
import {createChainedFunction} from "../../wui-core/src";
import {WebUI, getNid} from "../../wui-core/src/index";
import Collapse from "./Panel";

import {CollapseProps} from "./iCollapse";

const defaultProps = {
    accordion: false,
    bordered: false,
    ghost: false,
    expandIconPosition: "left",
    activeKey: null,
    defaultActiveKey: null,
    className: "",
    style: {},
    onSelect: null,
    collapsible: "",
    expandIcon: null,
    destroyInactivePanel: false
};

const CollapseGroup: React.FC<CollapseProps> & { Panel?: typeof Collapse } = (props) => {
    const [activeKey, setActiveKey] = useState<string | string[] | undefined>(() => {
        return props.defaultActiveKey || props.activeKey;
    });

    // 替换getDerivedStateFromProps，处理activeKey属性变化
    useEffect(() => {
        if ("activeKey" in props && props.activeKey !== activeKey && props.activeKey !== null) {
            setActiveKey(props.activeKey);
        }
    }, [props.activeKey, activeKey]);

    const handleSelect = useCallback((key: string, expandedFlag: boolean, e: React.MouseEvent<HTMLElement>) => {
        e?.preventDefault();

        if (props.onSelect) {
            props.onSelect(key, e);
        }

        if (props.activeKey) {
            return;
        }
        if (props.accordion) {
            setActiveKey(key);
            return;
        }
        let expandedFlagArr = [];
        // 点击展开面板时，将key存数组中，收起时从数组中删除当前key，更新activeKey
        if (!expandedFlag) {
            expandedFlagArr.push(key);
            if (typeof activeKey == "string" || typeof activeKey == "number") {
                expandedFlagArr.push(activeKey);
            } else {
                if (activeKey) {
                    expandedFlagArr = [...expandedFlagArr, ...activeKey];
                } else {
                    expandedFlagArr = [...expandedFlagArr];
                }
            }
            setActiveKey(expandedFlagArr);
        } else {
            if (typeof activeKey == "string" || typeof activeKey == "number") {
                expandedFlagArr.push(activeKey);
            } else {
                expandedFlagArr = [...(activeKey as string[])];
            }
            let setArr = new Set(expandedFlagArr);
            expandedFlagArr = [...setArr];
            let copyArr: any[] = [];
            expandedFlagArr.forEach(item => {
                if (item != key) {
                    copyArr.push(item);
                }
            });
            setActiveKey(copyArr);
        }
    }, [props.onSelect, props.activeKey, props.accordion, activeKey]);

    const {
        accordion,
        className,
        children,
        defaultActiveKey,
        clsPrefix,
        bordered,
        ghost,
        expandIconPosition,
        collapsible,
        expandIcon,
        destroyInactivePanel,
        bodyClassName,
        fieldid,
        type,
        ...others
    } = props;

    let currentActiveKey: any;
    if (accordion) {
        if (activeKey) {
            if (Array.isArray(activeKey)) {
                currentActiveKey = activeKey[0];
            } else {
                currentActiveKey = activeKey;
            }
        }
        others.role = others.role || "tablist";
    } else {
        if (typeof activeKey == "string" || typeof activeKey == "number") {
            let arr = [];
            arr.push(activeKey.toString());
            currentActiveKey = arr;
        } else {
            if (activeKey) {
                currentActiveKey = activeKey;
            } else {
                currentActiveKey = [];
            }
        }
    }

    let classes = {
        [`${clsPrefix}`]: true
    };
    let adapterNid = getNid(props);

    return (
        <div
            {...omit(others, ["activeKey", "onSelect", "style", "onChange"])}
            className={classNames(className, classes)}
            style={props.style}
            fieldid={fieldid}
            {...adapterNid}
        >
            {React.Children.map(children, child => {
                if (!React.isValidElement(child)) {
                    return child;
                }
                let paneKey = child.key === null ? child.props.eventKey : child.key;
                let childrenExpanded = child.props.expanded;
                const childProps = {
                    style: child.props.style,
                    bordered,
                    ghost,
                    expandIconPosition,
                    headerRole: "tab",
                    panelRole: "tabpanel",
                    // collapsible: true,
                    collapsible,
                    eventKey: paneKey,
                    expanded: childrenExpanded != undefined ? childrenExpanded : currentActiveKey?.includes(paneKey), // 子组件传入expanded时优先级高于activekey
                    parentFlag: true,
                    parentActiveKey: props.activeKey,
                    defaultActiveKey,
                    expandIcon,
                    destroyInactivePanel,
                    bodyClassName,
                    fieldid,
                    onSelect: createChainedFunction(handleSelect, child.props.onSelect)
                };
                let typeProps = {};
                if (type) {
                    typeProps = {
                        type
                    };
                }
                Object.assign(childProps, typeProps);

                if (accordion) {
                    Object.assign(childProps, {
                        collapsible,
                        accordion,
                        parentFlag: true,
                        expanded: paneKey === currentActiveKey
                    });
                }

                return cloneElement(child, childProps);
            })}
        </div>
    );
};

// 设置静态属性
CollapseGroup.Panel = Collapse;

export default WebUI({name: "collapse-group", defaultProps})(CollapseGroup);
